// import React, { forwardRef, useId } from "react";
// import clsx from "clsx";
// import styles from "./Radio.module.scss";
// import type { RadioProps } from "./Radio.types";

// /**
//  * Radio component
//  *
//  * A coustomizable radio button with support for:
//  * -Multiple sizes (sm,md,lg)
//  * -Multiple colors
//  * -Labels and description
//  * -Disabled and error state
//  * -Custom Colors
//  *
//  * @example
//  * <Radio name='option' value='1' label='option1'/>
//  * * @example
//  * // With description
//  * <Radio
//  *   name="plan"
//  *   value="pro"
//  *   label="Pro Plan"
//  *   description="$29/month, unlimited features"
//  * />
//  *
//  * @example
//  * // Controlled
//  * <Radio
//  *   name="choice"
//  *   value="yes"
//  *   isChecked={selected === 'yes'}
//  *   onChange={() => setSelected('yes')}
//  *   label="Yes"
//  * />
//  */

// export const Radio = forwardRef<HTMLInputElement, RadioProps>(
//   (
//     {
//       //Content
//       label,
//       description,
//       labelPosition = "right",
//       //Styling
//       size = "md",
//       color = "primary",
//       varient = "filled",
//       //State
//       isChecked,
//       isDisabled = false,
//       isError = false,
//       // Visibility
//       isRadioBtnShown = true, //  Default to true
//       //Custom colors
//       checkedColor,
//       borderColor,
//       //Html attributes
//       className,
//       id,
//       checked,
//       disabled,
//       onChange,
//       ...rest
//     },
//     ref,
//   ) => {
//     // Generate unique id
//     const generatedId = useId();
//     const radioId = id || generatedId;

//     //Computed Values
//     //Use isChecked prop or fallback to checked attribute
//     const isRadioChecked = isChecked !== undefined ? isChecked : checked;

//     //Use isDisabled prop or fallback to disabled attribute
//     const isRadioDisabled = isDisabled || disabled;

//     //Css custom variables
//     const customStyles = {
//       ...(checkedColor && { "--radio-checked-color": checkedColor }),
//       ...(borderColor && { "--radio-border": borderColor }),
//     } as React.CSSProperties;

//     const hasCustomStyle = checkedColor || borderColor;

//     // Wrapper classes (contains radio + label)
//     const wrapperClasses = clsx(
//       styles.radioWrapper,
//       styles[`size${size}`],
//       {
//         [styles.labelLeft]: labelPosition === "left",
//         [styles.labelRight]: labelPosition === "right",
//         [styles.isDisabled]: isDisabled,
//         [styles.isError]: isError,
//         [styles.hasDescription]: Boolean(description),
//         // [styles.radioHidden]: !isRadioBtnShown, // 👈 New class
//         // [styles.isChecked]: isRadioChecked && !isRadioBtnShown, // 👈 For styling when radio hidden
//       },
//       className,
//     );

//     // Radio input classes
//     const radioClasses = clsx(
//       styles.radio,
//       styles[`radio-${size}`],
//       styles[`${varient}-${color}`],
//       {
//         [styles.hasCustomStyle]: hasCustomStyle,
//         [styles.visuallyHidden]: !isRadioBtnShown, // 👈 Hide visually but keep accessible
//       },
//     );
//     const labelClasses = clsx(styles.label);
//     const descriptionClasses = clsx(styles.description);

//     return (
//       <label
//         className={wrapperClasses}
//         style={hasCustomStyle ? customStyles : undefined}
//         htmlFor={radioId}
//       >
//         {/**
//          * Radio Input
//          * The actual input element (hidden,replaced by styled element)
//          */}
//         <input
//           ref={ref}
//           type="radio"
//           id={radioId}
//           className={radioClasses}
//           checked={isRadioChecked}
//           disabled={isRadioDisabled}
//           aria-invalid={isError}
//           onChange={onChange}
//           {...rest}
//         />

//         {/*
//             LABEL & DESCRIPTION
//             Only rendered if label is provided
//             */}
//         {(label || description) && (
//           <div className={styles.labelContainer}>
//             {label && <span className={labelClasses}>{label}</span>}
//             {description && (
//               <span className={descriptionClasses}>{description}</span>
//             )}
//           </div>
//         )}
//       </label>
//     );
//   },
// );

// Radio.displayName = "Radio";

// Radio.tsx
import React, { forwardRef, useId } from "react";
import clsx from "clsx";
import styles from "./Radio.module.scss";
import type { RadioProps } from "./Radio.types";

/**
 * Radio component
 *
 * A customizable radio button with support for:
 * - Multiple sizes (sm, md, lg)
 * - Multiple colors (primary, secondary, success, danger, warning, info)
 * - Multiple variants (filled, outline)
 * - Labels and descriptions (supports ReactNode)
 * - Label positioning (left, right)
 * - Disabled and error states
 * - Custom colors via props
 * - Hidden radio button (card-style selection)
 * - Accessible (ARIA compliant)
 *
 * @example
 * // Basic usage
 * <Radio name="option" value="1" label="Option 1" />
 *
 * @example
 * // With description
 * <Radio
 *   name="plan"
 *   value="pro"
 *   label="Pro Plan"
 *   description="$29/month, unlimited features"
 * />
 *
 * @example
 * // Controlled
 * <Radio
 *   name="choice"
 *   value="yes"
 *   isChecked={selected === 'yes'}
 *   onChange={() => setSelected('yes')}
 *   label="Yes"
 * />
 *
 * @example
 * // Custom color
 * <Radio
 *   name="custom"
 *   value="gold"
 *   label="Gold Option"
 *   checkedColor="#FFC000CC"
 * />
 *
 * @example
 * // Outline variant with secondary color
 * <Radio
 *   name="outline"
 *   value="1"
 *   label="Outline Style"
 *   varient="outline"
 *   color="secondary"
 * />
 *
 * @example
 * // Hidden radio (card style)
 * <Radio
 *   name="card"
 *   value="option1"
 *   label="Card Option"
 *   description="Click to select"
 *   isRadioBtnShown={false}
 * />
 *
 * @example
 * // With ReactNode label
 * <Radio
 *   name="rich"
 *   value="1"
 *   label={<span><strong>Bold</strong> label</span>}
 * />
 */

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      // Content
      label,
      description,
      labelPosition = "right",

      // Visibility
      isRadioBtnShown = true,

      // Styling
      size = "md",
      color = "primary",
      varient = "filled",

      // State
      isChecked,
      isDisabled = false,
      isError = false,

      // Custom colors
      checkedColor,
      borderColor,

      // HTML attributes
      className,
      id,
      checked,
      disabled,
      onChange,
      ...rest
    },
    ref,
  ) => {
    // ============================================
    // HOOKS
    // ============================================

    const generatedId = useId();
    const radioId = id || generatedId;

    // ============================================
    // COMPUTED VALUES
    // ============================================

    // Use isChecked prop or fallback to native checked attribute
    const isRadioChecked = isChecked !== undefined ? isChecked : checked;

    // Use isDisabled prop or fallback to native disabled attribute
    const isRadioDisabled = isDisabled || disabled;

    // Determine if custom styles are applied
    const hasCustomStyle = Boolean(checkedColor || borderColor);

    // ============================================
    // CSS CUSTOM PROPERTIES
    // ============================================

    const customStyles = {
      ...(checkedColor && { "--radio-checked-color": checkedColor }),
      ...(borderColor && { "--radio-border": borderColor }),
    } as React.CSSProperties;

    // ============================================
    // CLASS NAMES
    // ============================================

    // Wrapper classes (outermost <label>)
    const wrapperClasses = clsx(
      styles.radioWrapper,
      styles[`size-${size}`],
      {
        // Label position
        [styles.labelLeft]: labelPosition === "left",
        [styles.labelRight]: labelPosition === "right",

        // State modifiers
        [styles.isDisabled]: isRadioDisabled,
        [styles.isError]: isError,
        [styles.hasDescription]: Boolean(description),

        // Hidden radio variant (card-style)
        [styles.radioHidden]: !isRadioBtnShown,
        [styles.isChecked]: isRadioChecked && !isRadioBtnShown,
      },
      className,
    );

    // Visual radio control classes (the styled <span>)
    const radioControlClasses = clsx(
      styles.radioControl,
      styles[`radio-${size}`],
      styles[`${varient}-${color}`],
      {
        [styles.hasCustomStyle]: hasCustomStyle,
        [styles.isChecked]: isRadioChecked,
      },
    );

    // ============================================
    // RENDER
    // ============================================

    return (
      <label
        className={wrapperClasses}
        style={hasCustomStyle ? customStyles : undefined}
        htmlFor={radioId}
      >
        {/*
         * ==========================================
         * NATIVE INPUT (visually hidden, accessible)
         * ==========================================
         * Always rendered for accessibility and form submission.
         * Visually hidden via .nativeInput class.
         * Keyboard navigation and screen readers interact
         * with this element.
         */}
        <input
          ref={ref}
          type="radio"
          id={radioId}
          className={styles.nativeInput}
          checked={isRadioChecked}
          disabled={isRadioDisabled}
          aria-invalid={isError || undefined}
          aria-describedby={description ? `${radioId}-description` : undefined}
          onChange={onChange}
          {...rest}
        />

        {/*
         * ==========================================
         * VISUAL RADIO CONTROL (<span>)
         * ==========================================
         * Only rendered when isRadioBtnShown is true.
         * Uses ::before pseudo-element for the inner dot.
         * (<input> elements cannot have pseudo-elements,
         * which is why we use a <span> instead.)
         * aria-hidden since the native input handles
         * all accessibility concerns.
         */}
        {isRadioBtnShown && (
          <span className={radioControlClasses} aria-hidden="true" />
        )}

        {/*
         * ==========================================
         * LABEL & DESCRIPTION
         * ==========================================
         * Rendered only if label or description is provided.
         * Both support ReactNode for rich content.
         * Description is linked to the input via
         * aria-describedby for screen readers.
         */}
        {(label || description) && (
          <div className={styles.labelContainer}>
            {label && <span className={styles.label}>{label}</span>}
            {description && (
              <span
                id={`${radioId}-description`}
                className={styles.description}
              >
                {description}
              </span>
            )}
          </div>
        )}
      </label>
    );
  },
);

Radio.displayName = "Radio";
